import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { withError } from '@/react-tools/react-query';

export type K8sGPUNodeStatus = 'Ready' | 'Degraded' | 'Not Available';

export interface K8sGPUSummary {
  TotalCapacity: Record<string, number>;
  TotalAllocatable: Record<string, number>;
  TotalAllocated: Record<string, number>;
  GPUNodeCount: number;
  DegradedCount: number;
  OperatorDetected: boolean;
  DevicePluginDetected: boolean;
}

export interface K8sGPUNode {
  Name: string;
  Capacity: Record<string, number>;
  Allocatable: Record<string, number>;
  Allocated: Record<string, number>;
  Status: K8sGPUNodeStatus;
  StatusReason: string;
}

export interface K8sGPUWorkload {
  Namespace: string;
  PodName: string;
  OwnerKind: string;
  OwnerName: string;
  GPURequests: Record<string, number>;
  NodeName: string;
  PodPhase: string;
  SchedulingIssue: string;
}

export interface K8sGPUInfo {
  Summary: K8sGPUSummary;
  Nodes: K8sGPUNode[];
  Workloads: K8sGPUWorkload[];
}

async function getGPUInfo(environmentId: EnvironmentId): Promise<K8sGPUInfo> {
  try {
    const { data } = await axios.get<K8sGPUInfo>(
      `/kubernetes/${environmentId}/gpu`
    );
    return data;
  } catch (e) {
    throw parseAxiosError(e);
  }
}

interface UseGpuQueryOptions {
  enabled?: boolean;
  showError?: boolean;
}

export function useGpuQuery(
  environmentId: EnvironmentId,
  { enabled = true, showError = true }: UseGpuQueryOptions = {}
) {
  return useQuery(
    ['environments', environmentId, 'kubernetes', 'gpu'],
    () => getGPUInfo(environmentId),
    {
      ...(showError && withError('Unable to retrieve GPU information')),
      enabled: !!environmentId && enabled,
    }
  );
}
